import React, { useCallback, useMemo } from "react";
import { Typography, makeStyles } from "@material-ui/core";
import {
	GetTwilioInvoiceDetailsListForDate,
	ReprocessTwilioInvoiceFile,
	UpdateTwilioInvoiceHistoryNote,
	GetTwilioInvoiceFileFromStorage,
	SubmitTwilioAccountingFile,
	CheckForNewerTwilioAccountingFileSent,
} from "../../services/api/twilioInvoice.api";
import { FileSetDetailResponse, InvoiceFileResponse } from "../../models/Invoice";
import { Fetcher } from "../../components/Fetcher";
import moment from "moment";
import { showSnack } from "../../utils/component";
import { useSnackbar } from "notistack";
import { AxiosResponse } from "axios";
import { InvoiceFileSetListContainer } from "../../components/InvoiceProcessing/InvoiceFileSetListContainer";
import { Link } from "react-router-dom";
import { toTwilioInvoiceListPage } from "../../router/navigation";

interface TwilioInvoiceDetailsPageProps {
	//The date for invoice sets to get.
	invoiceDate: string;
	//The id for the product to display the invoice sets for.
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	titleWrapper: {
		maxWidth: theme.breakpoints.values.md,
		display: "flex",
		justifyContent: "space-between",
	},
	title: {
		marginBottom: theme.spacing(3),
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	addFileButton: {
		marginRight: theme.spacing(2),
	},
	appBar: {
		position: "relative",
	},
	appBarTitle: {
		marginTop: "auto",
		marginBottom: "auto",
	},
	dialogContent: {
		maxWidth: "100%",
		overflow: "auto",
	},
}));

const renderPage = (
	data: FileSetDetailResponse[],
	handleReprocessing: (invoiceHistoryId: string) => void,
	handleFileSubmission: (invoiceHistoryId: string, accountingFileName: string) => void,
	retrieveFile: (invoiceHistoryId: string, fileName: string) => Promise<AxiosResponse<InvoiceFileResponse>>,
	updateNote: (invoiceHistoryId: string, newNote: string) => void,
	generateFileDownloadLink: (invoiceHistoryId: string, fileName: string) => string,
) => {
	return (
		<InvoiceFileSetListContainer
			fileSetList={data}
			handleReprocessing={handleReprocessing}
			retrieveFile={retrieveFile}
			updateNote={updateNote}
			generateFileDownloadLink={generateFileDownloadLink}
			handleFileSubmission={handleFileSubmission}
		></InvoiceFileSetListContainer>
	);
};

/**
 * This page is used to display the invoice details for twilio invoices.
 * @param props
 */
export const TwilioInvoiceDetailsPage = (props: TwilioInvoiceDetailsPageProps) => {
	const classes = useStyles();
	const invoiceDate = moment(props.invoiceDate);
	const { enqueueSnackbar } = useSnackbar();

	const [refresh, setForceRefresh] = React.useState(false);

	const forceRefresh = useCallback(() => {
		setForceRefresh(!refresh);
	}, [refresh]);

	const handleReprocessing = useCallback(
		async (invoiceHistoryId: string) => {
			const response = await ReprocessTwilioInvoiceFile(invoiceHistoryId, props.productId)();
			forceRefresh();
			const result = response.data;
			if (result) {
				showSnack(enqueueSnackbar, "The file was successfully processed.", "success");
			} else {
				showSnack(enqueueSnackbar, "An error occured while processing the file.", "error");
			}
		},
		[enqueueSnackbar, forceRefresh, props.productId],
	);

	const handleFileSubmission = useCallback(
		async (invoiceHistoryId: string, accountingFileName: string) => {
			const newer = await CheckForNewerTwilioAccountingFileSent(invoiceHistoryId, props.productId)();
			const confirmMessage =
				`Are you sure you want to submit: ${accountingFileName}?` +
				(newer.data ? "\n A newer file has already been submitted for this date." : "");
			if (confirm(confirmMessage)) {
				const response = await SubmitTwilioAccountingFile(invoiceHistoryId, props.productId)();
				forceRefresh();
				const result = response.data;
				if (result) {
					showSnack(enqueueSnackbar, "The file was successfully submitted.", "success");
				} else {
					showSnack(enqueueSnackbar, "An error occured while submitting the file.", "error");
				}
			}
		},
		[enqueueSnackbar, forceRefresh, props.productId],
	);

	const handleNoteChange = useCallback(
		async (invoiceHistoryId: string, newNote: string) => {
			const response = await UpdateTwilioInvoiceHistoryNote(invoiceHistoryId, newNote.trim(), props.productId)();
			forceRefresh();
			const result = response.data;
			if (result) {
				showSnack(enqueueSnackbar, "The note was successfully updated.", "success");
			} else {
				showSnack(enqueueSnackbar, "An error occured while attempting to update the note.", "error");
			}
		},
		[enqueueSnackbar, forceRefresh, props.productId],
	);

	const retrieveFile = useCallback(
		async (invoiceHistoryId: string, fileName: string) => {
			return GetTwilioInvoiceFileFromStorage(invoiceHistoryId, fileName, props.productId);
		},
		[props.productId],
	);

	const generateFileDownloadLink = useCallback(
		(invoiceHistoryId: string, fileName: string) => {
			return `/api/v1/${props.productId}/twilio/invoices/${invoiceHistoryId}/${fileName}/download`;
		},
		[props.productId],
	);

	return (
		<div data-testid="twilio-invoice-details-page">
			<div className={classes.titleWrapper}>
				<Typography className={classes.title} variant="h4" data-testid="twilio-invoices-page">
					Twilio Invoices For {invoiceDate.month() + 1}/{invoiceDate.date()}/{invoiceDate.year()}
				</Typography>
			</div>
			<div style={{ marginTop: 25, marginBottom: 25 }}>
				<Link to={toTwilioInvoiceListPage(props.productId)}>Back to Invoice List</Link>
			</div>
			{useMemo(() => {
				return (
					<Fetcher action={GetTwilioInvoiceDetailsListForDate(props.invoiceDate, props.productId)}>
						{(response) =>
							renderPage(
								response && response.data,
								handleReprocessing,
								handleFileSubmission,
								retrieveFile,
								handleNoteChange,
								generateFileDownloadLink,
							)
						}
					</Fetcher>
				);
			}, [
				generateFileDownloadLink,
				handleFileSubmission,
				handleNoteChange,
				handleReprocessing,
				props.invoiceDate,
				props.productId,
				retrieveFile,
			])}
		</div>
	);
};
